import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  FILTER_OPTIONS_LIST_GAP_PX,
  availableFilterOptionsMaxHeight,
  filterOptionsBySearch,
  isSpecialFilterOption,
  shouldUseSearchableFilter,
} from '@/utils/recentsFilterOptions';

type FilterValueSelectProps = {
  id: string;
  columnName: string;
  value: string;
  options: string[];
  disabled?: boolean;
  loading?: boolean;
  onChange: (columnName: string, value: string) => void;
  ariaLabel?: string;
  title?: string;
  /** Focus the control when it mounts (e.g. after clicking the filter icon). */
  autoFocus?: boolean;
};

function optionClassName(option: string): string {
  return isSpecialFilterOption(option) ? 'filter-option special' : 'filter-option';
}

function optionDomId(listboxId: string, index: number): string {
  return `${listboxId}-opt-${index}`;
}

export default function FilterValueSelect({
  id,
  columnName,
  value,
  options,
  disabled = false,
  loading = false,
  onChange,
  ariaLabel,
  title,
  autoFocus = false,
}: FilterValueSelectProps) {
  const label = ariaLabel || title || `Filter ${columnName}`;
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchable = shouldUseSearchableFilter(options);
  const visibleOptions = searchable ? filterOptionsBySearch(options, search) : [];
  const navigableValues = searchable ? ['', ...visibleOptions] : [];

  useEffect(() => {
    if (!autoFocus || disabled || loading) return;
    const el = searchable ? inputRef.current : selectRef.current;
    el?.focus();
  }, [autoFocus, searchable, disabled, loading]);

  useEffect(() => {
    if (!open) return undefined;
    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open || !searchable) {
      setHighlightedIndex(-1);
      return;
    }
    const items = ['', ...filterOptionsBySearch(options, search)];
    if (String(search ?? '').trim()) {
      setHighlightedIndex(items.length > 0 ? 0 : -1);
      return;
    }
    const idx = items.indexOf(value || '');
    setHighlightedIndex(idx >= 0 ? idx : items.length > 0 ? 0 : -1);
  }, [open, searchable, search, options, value]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const root = rootRef.current;
    const list = listRef.current;
    if (!root || !list) return undefined;

    function applyPlacement() {
      const trigger = root.getBoundingClientRect();
      const listTop = trigger.bottom + FILTER_OPTIONS_LIST_GAP_PX;
      const px = availableFilterOptionsMaxHeight(listTop, window.innerHeight);
      if (px > 0) {
        list.style.setProperty('--filter-options-max-height', `${px}px`);
      }
      list.style.setProperty('--filter-options-top', `${Math.round(listTop)}px`);
      list.style.setProperty('--filter-options-left', `${Math.round(trigger.left)}px`);
      list.style.setProperty('--filter-options-min-width', `${Math.round(trigger.width)}px`);
    }

    applyPlacement();
    window.addEventListener('resize', applyPlacement);
    window.addEventListener('scroll', applyPlacement, true);
    return () => {
      window.removeEventListener('resize', applyPlacement);
      window.removeEventListener('scroll', applyPlacement, true);
    };
  }, [open, options.length, search]);

  useLayoutEffect(() => {
    if (!open || highlightedIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-option-index="${highlightedIndex}"]`
    );
    if (typeof el?.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, open, search]);

  if (!searchable) {
    return (
      <select
        ref={selectRef}
        id={id}
        className="filter-select"
        value={value || ''}
        data-column={columnName}
        title={title || label}
        aria-label={label}
        disabled={disabled || loading}
        onChange={(e) => onChange(columnName, e.target.value)}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="">{loading ? 'Loading...' : 'All'}</option>
        {(options || []).map((option, i) => (
          <option
            key={`${columnName}-${i}-${option}`}
            value={option}
            className={optionClassName(option)}
          >
            {option}
          </option>
        ))}
      </select>
    );
  }

  const displayValue = open ? search : value || '';
  const placeholder = loading ? 'Loading...' : value ? value : 'All — type to filter';

  function selectValue(next: string) {
    onChange(columnName, next);
    setOpen(false);
    setSearch('');
    setHighlightedIndex(-1);
  }

  function moveHighlight(delta: number) {
    setOpen(true);
    setHighlightedIndex((current) => {
      const max = navigableValues.length - 1;
      if (max < 0) return -1;
      if (current < 0) return delta > 0 ? 0 : max;
      return Math.max(0, Math.min(max, current + delta));
    });
  }

  function handleInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
      setHighlightedIndex(-1);
      (e.target as HTMLInputElement).blur();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveHighlight(1);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight(-1);
      return;
    }

    if (e.key === 'Home' && open && navigableValues.length > 0) {
      e.preventDefault();
      setHighlightedIndex(0);
      return;
    }

    if (e.key === 'End' && open && navigableValues.length > 0) {
      e.preventDefault();
      setHighlightedIndex(navigableValues.length - 1);
      return;
    }

    if (e.key === 'Enter' && open) {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < navigableValues.length) {
        selectValue(navigableValues[highlightedIndex]);
      } else if (visibleOptions.length === 1) {
        selectValue(visibleOptions[0]);
      }
    }
  }

  return (
    <div
      ref={rootRef}
      className={`filter-value-select${open ? ' open' : ''}${value ? ' has-value' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        id={id}
        type="text"
        className="filter-select filter-search-input"
        value={displayValue}
        placeholder={placeholder}
        title={title || label}
        aria-label={label}
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={
          open && highlightedIndex >= 0
            ? optionDomId(listboxId, highlightedIndex)
            : undefined
        }
        aria-autocomplete="list"
        aria-haspopup="listbox"
        role="combobox"
        disabled={disabled || loading}
        autoComplete="off"
        onFocus={() => {
          setOpen(true);
          setSearch('');
        }}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onKeyDown={handleInputKeyDown}
      />
      {open && (
        <ul ref={listRef} id={listboxId} className="filter-options-list" role="listbox">
          <li
            id={optionDomId(listboxId, 0)}
            role="option"
            data-option-index={0}
            className={`filter-option all${value === '' ? ' selected' : ''}${
              highlightedIndex === 0 ? ' highlighted' : ''
            }`}
            aria-selected={value === ''}
            onMouseEnter={() => setHighlightedIndex(0)}
            onMouseDown={(e) => {
              e.preventDefault();
              selectValue('');
            }}
          >
            All
          </li>
          {visibleOptions.map((option, i) => {
            const index = i + 1;
            return (
              <li
                key={`${columnName}-${option}`}
                id={optionDomId(listboxId, index)}
                role="option"
                data-option-index={index}
                className={`${optionClassName(option)}${value === option ? ' selected' : ''}${
                  highlightedIndex === index ? ' highlighted' : ''
                }`}
                aria-selected={value === option}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectValue(option);
                }}
              >
                {option}
              </li>
            );
          })}
          {visibleOptions.length === 0 && (
            <li className="filter-option empty" role="presentation">
              No matches
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
